/* The Founder Console's detail views. Same evidence, more of it. */
import { useState } from "react";
import axios from "axios";
import {
  Users, Heart, BarChart3, Target, MessageSquare, Activity, FileText, RotateCcw, Layers,
  ThumbsUp, ThumbsDown, Link2,
} from "lucide-react";
import { Panel, MetricCard, Figure, Reason, Delta, InfoDot, Chip, Note, fmt, DASH } from "./ConsolePrimitives";
import { HistoryToggle } from "./HistoryToggle";
import {
  HeadlineCards, GrowthPanel, DistributionPanel, EngagementPanel, UsagePanel, SystemPanel,
  RadarPanel, FeedbackPanel,
} from "./OverviewPanels";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/founder`;

const Row = ({ label, env, metric, catalog, comparison, suffix = "" }) => (
  <tr data-testid={`fc-row-${metric}`}>
    <td><b>{label}</b><InfoDot metric={metric} catalog={catalog} /></td>
    <td><Figure env={env} suffix={suffix} className="" testId={`fc-row-${metric}-value`} /></td>
    <td><Delta env={env} comparison={comparison} /><Reason env={env} /></td>
    <td><Chip tone="quiet">{env?.basis || DASH}</Chip></td>
  </tr>
);

export const MembersView = ({ members, catalog, comparison }) => (
  <>
    <HeadlineCards members={members} catalog={catalog} comparison={comparison} />
    <div className="fc__row fc__row--mid">
      <GrowthPanel growth={members.growth} window={{ label: comparison }} catalog={catalog} />
      <DistributionPanel members={members} catalog={catalog} />
    </div>
    <Panel title="Every member definition, stated" icon={Users} testId="fc-members-table"
      sub="Nothing here is shown without its definition, its source and the population it covers.">
      <table className="fc-table">
        <thead><tr><th>Metric</th><th>Value</th><th>Movement</th><th>Basis</th></tr></thead>
        <tbody>
          <Row label="Total members" env={members.total_members} metric="total_members" catalog={catalog} comparison={comparison} />
          <Row label="New members" env={members.new_members} metric="new_members" catalog={catalog} comparison={comparison} />
          <Row label="Premium members" env={members.premium_members} metric="premium_members" catalog={catalog} comparison={comparison} />
          <Row label="Trial members" env={members.trial_members} metric="trial_members" catalog={catalog} comparison={comparison} />
          <Row label="Free members" env={members.free_members} metric="free_members" catalog={catalog} comparison={comparison} />
          <Row label="Cancelled (current state)" env={members.canceled_members} metric="canceled_members" catalog={catalog} comparison={comparison} />
          <Row label="Past due" env={members.past_due_members} metric="past_due_members" catalog={catalog} comparison={comparison} />
          <Row label="Returning members" env={members.returning_members} metric="returning_members" catalog={catalog} comparison={comparison} />
          <Row label="Returning member rate" env={members.returning_member_rate} metric="returning_member_rate" catalog={catalog} comparison={comparison} suffix="%" />
          <Row label="Activated members" env={members.activated_members} metric="activated_members" catalog={catalog} comparison={comparison} />
          <Row label="Trials started" env={members.trials_started} metric="trials_started" catalog={catalog} comparison={comparison} />
          <Row label="Trial to paid" env={members.trial_to_paid} metric="trial_to_paid" catalog={catalog} comparison={comparison} suffix="%" />
        </tbody>
      </table>
      <Note testId="fc-members-population">{members.population?.note}</Note>
      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
        <Chip testId="fc-excluded-total">{members.population?.excluded_total} excluded by rule</Chip>
        {Object.entries(members.population?.excluded_by_rule || {}).map(([rule, n]) => (
          <Chip key={rule} tone="quiet" testId={`fc-excluded-${rule}`}>{rule}: {n}</Chip>
        ))}
        <Chip tone="quiet" testId="fc-excluded-deleted">{members.population?.deleted_excluded} deleted</Chip>
      </div>
    </Panel>
  </>
);

export const RelationshipView = ({ health, members, catalog, comparison }) => (
  <>
    <EngagementPanel health={health} members={members} catalog={catalog} comparison={comparison} />
    <div className="fc__row fc__row--2">
      <Panel title="Surface adoption" icon={Layers} testId="fc-adoption-panel">
        <table className="fc-table">
          <thead><tr><th>Metric</th><th>Value</th><th>Movement</th><th>Basis</th></tr></thead>
          <tbody>
            <Row label="Message adopters" env={health.message_adopters} metric="message_adopters" catalog={catalog} comparison={comparison} />
            <Row label="Talk adopters" env={health.talk_adopters} metric="talk_adopters" catalog={catalog} comparison={comparison} />
            <Row label="Dream adopters" env={health.dream_adopters} metric="dream_adopters" catalog={catalog} comparison={comparison} />
            <Row label="Multi-surface members" env={health.multi_surface_members} metric="multi_surface_members" catalog={catalog} comparison={comparison} />
            <Row label="Multi-surface rate" env={health.multi_surface_rate} metric="multi_surface_rate" catalog={catalog} comparison={comparison} suffix="%" />
          </tbody>
        </table>
      </Panel>
      <Panel title="Member control, and continuity" icon={RotateCcw} testId="fc-control-panel">
        <table className="fc-table">
          <thead><tr><th>Metric</th><th>Value</th><th>Movement</th><th>Basis</th></tr></thead>
          <tbody>
            <Row label="Memory corrections" env={health.memory_corrections} metric="memory_corrections" catalog={catalog} comparison={comparison} />
            <Row label="Memory forgets" env={health.memory_forgets} metric="memory_forgets" catalog={catalog} comparison={comparison} />
            <Row label="Members holding a forgotten item" env={health.members_with_forgotten_items} metric="members_with_forgotten_items" catalog={catalog} comparison={comparison} />
            <Row label="Interrupted Talks" env={health.interrupted_talks} metric="saved_talks" catalog={catalog} comparison={comparison} />
          </tbody>
        </table>
        <Note testId="fc-control-neutrality">{health.neutrality}</Note>
        <Note testId="fc-control-separation">{health.separation}</Note>
      </Panel>
    </div>
  </>
);

// F4b — blocked ATTEMPTS at the two usage-limit gates (attempts, not members).
const BlockedAttempts = ({ blocked }) => !blocked ? null : (
  <Panel title="Blocked attempts at usage limits" icon={BarChart3} testId="fc-blocked-attempts-panel"
    sub="Attempts, not members — one member can make many.">
    {Object.entries(blocked).map(([key, b]) => (
      <p key={key} data-testid={`fc-blocked-${key}`}>
        {key === "message_allowance" ? "Message" : "Talk"}: {b.suppressed ? "withheld" : fmt(b.attempts)} attempts
        {b.by_plan ? ` · ${b.by_plan.map((p) => `${p.plan} ${fmt(p.attempts)}`).join(" · ")}` : " · plan split withheld"}
      </p>
    ))}
  </Panel>
);

export const UsageView = ({ usage, catalog, comparison }) => (
  <>
    <UsagePanel usage={usage} catalog={catalog} comparison={comparison} />
    <BlockedAttempts blocked={usage.blocked_attempts} />
    <Panel title="Allowance pressure" icon={BarChart3} testId="fc-capacity-panel"
      sub="Operational capacity evidence. It reaches no member.">
      <table className="fc-table">
        <thead><tr><th>Metric</th><th>Value</th><th>Movement</th><th>Basis</th></tr></thead>
        <tbody>
          <Row label="Message utilisation" env={usage.message_utilization} metric="message_utilization" catalog={catalog} comparison={comparison} suffix="%" />
          <Row label="Talk utilisation" env={usage.talk_utilization} metric="talk_utilization" catalog={catalog} comparison={comparison} suffix="%" />
          <Row label="Members at 80% or more" env={usage.members_near_allowance} metric="members_near_allowance" catalog={catalog} comparison={comparison} />
          <Row label="Members at 100% or more" env={usage.members_at_allowance} metric="members_at_allowance" catalog={catalog} comparison={comparison} />
        </tbody>
      </table>
      <Note testId="fc-capacity-coverage">{usage.metering_coverage}</Note>
      <Note testId="fc-capacity-guard">{usage.commercial_guard}</Note>
    </Panel>
  </>
);

export const SystemView = ({ system, catalog, comparison }) => (
  <>
    <SystemPanel system={system} catalog={catalog} />
    <div className="fc__row fc__row--2">
      <Panel title="Talk connection" icon={Activity} testId="fc-talk-health-panel">
        <table className="fc-table">
          <thead><tr><th>Metric</th><th>Value</th><th>Movement</th><th>Basis</th></tr></thead>
          <tbody>
            <Row label="Connection attempts" env={system.talk_connect_attempts} metric="talk_connect_attempts" catalog={catalog} comparison={comparison} />
            <Row label="Established rate" env={system.talk_connect_success_rate} metric="talk_connect_success_rate" catalog={catalog} comparison={comparison} suffix="%" />
            <Row label="Failures" env={system.talk_connect_failures} metric="talk_connect_failures" catalog={catalog} comparison={comparison} />
          </tbody>
        </table>
        {(system.talk_failure_stages || []).length > 0 && (
          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
            {system.talk_failure_stages.map((s) => (
              <Chip key={s.stage} tone="warn" testId={`fc-talk-stage-${s.stage}`}>{s.stage}: {s.count}</Chip>
            ))}
          </div>
        )}
        <Note>{system.separation}</Note>
      </Panel>
      <Panel title="Capabilities, providers and billing" icon={Target} testId="fc-provider-panel">
        <table className="fc-table">
          <thead><tr><th>Metric</th><th>Value</th><th>Movement</th><th>Basis</th></tr></thead>
          <tbody>
            <Row label="Capability attempts" env={system.capability_attempts} metric="capability_attempts" catalog={catalog} comparison={comparison} />
            <Row label="Capability failure rate" env={system.capability_failure_rate} metric="capability_failure_rate" catalog={catalog} comparison={comparison} suffix="%" />
            <Row label="Average latency" env={system.capability_avg_latency_ms} metric="capability_avg_latency_ms" catalog={catalog} comparison={comparison} suffix="ms" />
            <Row label="API uptime" env={system.api_uptime} metric="api_uptime" catalog={catalog} comparison={comparison} />
            <Row label="Whole-application error rate" env={system.error_rate} metric="error_rate" catalog={catalog} comparison={comparison} />
            <Row label="Stripe webhook events" env={system.stripe_events} metric="stripe_events" catalog={catalog} comparison={comparison} />
            <Row label="Billing transactions" env={system.billing_transactions} metric="billing_transactions" catalog={catalog} comparison={comparison} />
          </tbody>
        </table>
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          {(system.stripe_events_by_type || []).map((e) => (
            <Chip key={e.type} tone="good" testId={`fc-webhook-${e.type}`}>{e.type}: {e.count}</Chip>
          ))}
          {(system.capability_latency_by_category || []).map((c) => (
            <Chip key={c.category} tone="quiet">{c.category}: {c.avg_ms === null ? DASH : `${c.avg_ms}ms`}</Chip>
          ))}
        </div>
      </Panel>
    </div>
    <Panel title="Browser runtime" icon={Activity} testId="fc-client-runtime-panel"
      sub="Counts by stage and category from the app's own diagnostics. No error message, browser detail or member is read.">
      <table className="fc-table">
        <thead><tr><th>Metric</th><th>Value</th><th>Movement</th><th>Basis</th></tr></thead>
        <tbody>
          <Row label="Runtime errors" env={system.client_runtime_errors} metric="client_runtime_errors" catalog={catalog} comparison={comparison} />
          <Row label="Page reloads (not errors)" env={system.client_page_reloads} metric="client_page_reloads" catalog={catalog} comparison={comparison} />
        </tbody>
      </table>
      <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
        {(system.client_runtime_by_stage || []).map((s) => (
          <Chip key={`${s.stage}-${s.category}`} tone="quiet" testId={`fc-client-stage-${s.stage}-${s.category}`}>
            {s.stage}/{s.category}: {s.suppressed ? "withheld" : s.count}
          </Chip>
        ))}
      </div>
    </Panel>
  </>
);

export const RadarView = ({ radar, catalog, comparison, onReload, canDispose = true }) => {
  const [busy, setBusy] = useState(null);
  const setState = async (key, state) => {
    setBusy(key);
    try {
      await axios.post(`${API}/capability-signals/${encodeURIComponent(key)}/disposition`, { state });
      await onReload();
    } finally {
      setBusy(null);
    }
  };
  return (
    <>
      <Panel title="Capability Radar" icon={Target} testId="fc-radar-view"
        sub="What members repeatedly attempt that Arvexis cannot yet do reliably — and where you left it.">
        <table className="fc-table">
          <thead>
            <tr><th>Signal</th><th>Events</th><th>Members</th><th>Evidence</th><th>Your disposition</th></tr>
          </thead>
          <tbody>
            {(radar.signals || []).length === 0 && (
              <tr><td colSpan={5} className="fc-unavail" data-testid="fc-radar-view-empty">
                No capability signal was recorded in this period.
              </td></tr>
            )}
            {(radar.signals || []).map((s) => (
              <tr key={s.signal_key} data-testid={`fc-radar-signal-${s.signal_key}`}>
                <td>
                  <b>{s.category} / {s.operation}</b>
                  <div style={{ fontSize: 11.5, color: "var(--fc-mute)", marginTop: 3 }}>
                    {s.surface} · {s.reason_code || "no reason recorded"}
                  </div>
                </td>
                <td>
                  <Figure env={s.events} className="" testId={`fc-radar-events-${s.signal_key}`} />
                  <Delta env={s.events} comparison={comparison} />
                </td>
                <td>
                  <Figure env={s.affected_members} className="" testId={`fc-radar-members-${s.signal_key}`} />
                  <Reason env={s.affected_members} testId={`fc-radar-members-reason-${s.signal_key}`} />
                </td>
                <td>
                  <Chip tone={s.evidence.sufficient_evidence ? "good" : "quiet"} testId={`fc-radar-evidence-${s.signal_key}`}>
                    {s.evidence.sufficient_evidence ? "sufficient" : "insufficient"}
                  </Chip>
                  <div style={{ fontSize: 11.5, color: "var(--fc-mute)", marginTop: 5 }}>
                    {s.evidence.event_count} now vs {s.evidence.prior_event_count} in {s.evidence.comparison_period}
                    {s.evidence.insufficient_reason ? ` — ${s.evidence.insufficient_reason}` : ""}
                  </div>
                </td>
                <td>
                  <select
                    className="fc-state-select"
                    data-testid={`fc-radar-state-${s.signal_key}`}
                    disabled={busy === s.signal_key || !canDispose}
                    title={canDispose ? undefined : "You are not authorized to change a disposition."}
                    value={s.lifecycle.state}
                    onChange={(e) => setState(s.signal_key, e.target.value)}
                  >
                    {(radar.lifecycle_states || []).map((st) => (
                      <option key={st} value={st}>{st.replace("_", " ")}</option>
                    ))}
                  </select>
                  <HistoryToggle url={`${API}/capability-signals/${encodeURIComponent(s.signal_key)}/history`}
                    testId={`fc-radar-history-${s.signal_key}`} describe={(h) => `${h.action} → ${h.state}`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Note testId="fc-radar-view-coverage">{radar.coverage?.note}</Note>
        <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
          <Chip tone="quiet">{radar.coverage?.capability_attempts_in_window} capability attempts in period</Chip>
          <Chip tone="quiet">{radar.coverage?.signal_attempts_in_window} of them were failures or unsupported</Chip>
          <Chip tone="quiet">small-N threshold: {radar.small_n?.threshold}</Chip>
          <Chip tone="quiet">telemetry retention: {radar.coverage?.retention_days} days</Chip>
        </div>
      </Panel>
    </>
  );
};

export const FeedbackView = ({ feedback, catalog, comparison }) => (
  <>
    {feedback.not_helpful_reasons && (
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }} data-testid="fc-not-helpful-reasons">
        {feedback.not_helpful_reasons.map((r) => (
          <Chip key={r.reason} tone="quiet" testId={`fc-reason-${r.reason}`}>
            {r.reason.replaceAll("_", " ")}: {r.suppressed ? "withheld" : `${fmt(r.members)} members`}
          </Chip>
        ))}
      </div>
    )}
    <div className="fc__row fc__row--5">
      <MetricCard icon={MessageSquare} label="Total verdicts" env={feedback.explicit_feedback_total} metric="explicit_feedback_total" catalog={catalog} comparison={comparison} testId="fc-card-feedback-total" />
      <MetricCard icon={Heart} label="Confirmed" env={feedback.explicit_feedback_confirmed} metric="explicit_feedback_confirmed" catalog={catalog} comparison={comparison} testId="fc-card-feedback-confirmed" />
      <MetricCard icon={RotateCcw} label="Corrected" env={feedback.explicit_feedback_corrected} metric="explicit_feedback_corrected" catalog={catalog} comparison={comparison} testId="fc-card-feedback-corrected" />
    </div>
    <div className="fc__row fc__row--5">
      <MetricCard icon={ThumbsUp} label="Replies marked helpful" env={feedback.response_feedback_helpful} metric="response_feedback_helpful" catalog={catalog} comparison={comparison} testId="fc-card-response-helpful" />
      <MetricCard icon={ThumbsDown} label="Replies marked not helpful" env={feedback.response_feedback_not_helpful} metric="response_feedback_not_helpful" catalog={catalog} comparison={comparison} testId="fc-card-response-not-helpful" />
      <MetricCard icon={Link2} label="Resolvable to a stored reply" env={feedback.response_feedback_attributable} metric="response_feedback_attributable" catalog={catalog} comparison={comparison} testId="fc-card-response-attributable" />
      <MetricCard icon={Layers} label="Pre-fix rows (no withdrawal)" env={feedback.response_feedback_legacy_rows} metric="response_feedback_legacy_rows" catalog={catalog} comparison={comparison} testId="fc-card-response-legacy" />
    </div>
    <Panel title="What counts as feedback here" icon={MessageSquare} testId="fc-feedback-view">
      <Note testId="fc-feedback-view-mechanism">{feedback.mechanism}</Note>
      <Note testId="fc-feedback-view-neutrality">{feedback.neutrality}</Note>
    </Panel>
  </>
);

export const BriefView = ({ brief }) => (
  <>
    <Panel title="Founder Brief" icon={FileText} testId="fc-brief-view"
      sub={`${brief.window?.label} · compared with ${brief.window?.comparison}`}>
      <h3 className="fc-brief__h">At a glance</h3>
      <p className="fc-brief__p" data-testid="fc-brief-view-glance">{brief.at_a_glance}</p>
      <h3 className="fc-brief__h" style={{ marginTop: 22 }}>Areas for your attention</h3>
      {(brief.attention || []).length === 0 ? (
        <p className="fc-brief__p" data-testid="fc-brief-view-attention-empty">{brief.attention_note}</p>
      ) : (
        <ul className="fc-brief__list">
          {brief.attention.map((a) => (
            <li className="fc-brief__item" key={a.title} data-testid={`fc-brief-view-attention-${a.rank}`}>
              <span className="fc-brief__num">{a.rank}</span>
              <span>
                <b style={{ color: "var(--fc-ivory)", fontWeight: 400 }}>{a.title}</b> — {a.recommendation}
                <span style={{ display: "block", color: "var(--fc-mute)", fontSize: 11.5, marginTop: 4 }}>
                  evidence: {Object.entries(a.evidence || {}).map(([k, v]) => `${k}=${v === null ? DASH : v}`).join(" · ")} · metric: {a.metric}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
    <Panel title="Every claim, and what it rests on" icon={FileText} testId="fc-brief-claims">
      {(brief.claims || []).map((c, i) => (
        <div className="fc-claim" key={i} data-testid={`fc-brief-claim-${i}`}>
          <span className="fc-claim__kind" data-kind={c.kind}>{c.kind}</span>
          <span>
            <span className="fc-claim__text">{c.text}</span>
            {c.evidence && (
              <span className="fc-claim__ev" data-testid={`fc-brief-claim-evidence-${i}`}>
                metric: {c.metric} · value: {c.evidence.value === null ? DASH : fmt(c.evidence.value)}
                {c.evidence.prior !== null && c.evidence.prior !== undefined ? ` · prior: ${fmt(c.evidence.prior)}` : ""}
                {c.evidence.denominator ? ` · denominator: ${c.evidence.denominator}` : ""}
                {" · basis: "}{c.evidence.basis}
                {c.evidence.sufficient === false ? " · evidence insufficient" : ""}
              </span>
            )}
          </span>
        </div>
      ))}
      <Note testId="fc-brief-grounding">{brief.grounding?.contract}</Note>
    </Panel>
  </>
);
